import type { Entity } from '@backstage/catalog-model';
import {
  Box,
  Card,
  CardContent,
  makeStyles,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import { DOCUMENTATION_TABS } from '../../constants';
import { getDocumentationType, getOwnerLabel, getTags } from '../../docsUtils';

export const DocsCard = ({ entity }: { entity: Entity }) => {
  const classes = useStyles();
  const documentationType = getDocumentationType(entity);

  const theme = useTheme();
  const DEFAULT_ACCENT = theme.palette.primary.main;

  const tab = DOCUMENTATION_TABS.find(item => item.key === documentationType);

  const accent = tab?.accent ?? DEFAULT_ACCENT;
  const namespace = entity.metadata.namespace || 'default';
  const docsUrl = `/docs/${namespace}/${entity.kind.toLowerCase()}/${
    entity.metadata.name
  }`;
  const tags = getTags(entity);

  return (
    <Link to={docsUrl} className={classes.link}>
      <Card className={classes.card} style={{ borderLeftColor: accent }}>
        <CardContent className={classes.content}>
          <Typography className={classes.title} component="div">
            {entity.metadata.title || entity.metadata.name}
          </Typography>
          {entity.metadata.description && (
            <Typography className={classes.description} component="p">
              {entity.metadata.description}
            </Typography>
          )}
          {documentationType === 'documentation' && (
            <Typography variant="caption" className={classes.owner}>
              {getOwnerLabel(entity)}
            </Typography>
          )}
          <Tooltip title={tags.join(', ')}>
            <Box className={classes.metadata}>
              {tags.slice(0, 3).map(tag => (
                <Box key={tag} className={classes.tag}>
                  <Typography className={classes.tagText}>{tag}</Typography>
                </Box>
              ))}
            </Box>
          </Tooltip>
        </CardContent>
      </Card>
    </Link>
  );
};

const useStyles = makeStyles(theme => ({
  link: {
    minWidth: 0,
    textDecoration: 'none',
  },
  card: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border}`,
    borderLeft: '3px solid transparent',
    borderRadius: 8,
    boxShadow: 'none',
    boxSizing: 'border-box',
    height: '100%',
    minHeight: 78,
    transition: 'border-color 0.2s ease, background-color 0.2s ease',
    width: '100%',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.info.main,
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: `${theme.spacing(2)}px !important`,
  },
  badges: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  lifecycle: {
    border: `1px solid ${theme.palette.border}`,
    borderRadius: 4,
    color: theme.palette.text.secondary,
    fontSize: 11,
    fontWeight: 600,
    padding: theme.spacing(0.25, 0.75),
  },
  title: {
    color: theme.palette.text.primary,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  description: {
    color: theme.palette.text.secondary,
    display: '-webkit-box',
    fontSize: '0.84rem',
    lineHeight: 1.45,
    marginTop: theme.spacing(0.5),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  },
  metadata: {
    alignItems: 'flex-start',
    color: theme.palette.text.secondary,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.25),
  },
  owner: {
    fontWeight: 600,
  },
  tag: {
    borderRadius: 4,
    border: `1px solid ${theme.palette.border}`,
    padding: theme.spacing(0.25, 0.75),
  },
  tagText: {
    color: theme.palette.text.primary,
    fontSize: 11,
  },
}));
